import { useTranslation } from "react-i18next";

import { PrimaryButton } from "components/Button/PrimaryButton";
import { PlanBadge, Plan } from "pages/Backoffice/TEST/PlanSelection";
import {
  SubscriptionResponse,
  getSubscriptionState,
  useChangeSubscription,
  useOpenBillingPortal,
} from "hooks/useStripe";
import { getRelativeTimeFuture } from "utils/date";
import { useHistory } from "react-router";

interface Props {
  subscription: SubscriptionResponse | null | undefined;
}

export const SubscriptionStatusBanner = ({ subscription }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { changeSubscription, isLoading: isReactivating } =
    useChangeSubscription();
  const { openBillingPortal, isLoading: isOpeningPortal } =
    useOpenBillingPortal();

  const uiState = getSubscriptionState(subscription ?? null);

  if (uiState === "free") {
    return null;
  }

  if (uiState === "trial_active") {
    const trialEndLabel = getRelativeTimeFuture(
      (subscription?.trailEnd || 0) * 1000
    );

    return (
      <div className="rounded-md bg-cyan-50 p-4 ring-1 ring-inset ring-cyan-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-cyan-900">
                {t("settings.billing.status_trial")}
              </span>
              {subscription?.plan && (
                <PlanBadge plan={subscription.plan as unknown as Plan} />
              )}
            </div>
            <p className="mt-1 text-sm text-cyan-700">
              {t("settings.billing.trial_label")}: {trialEndLabel}
            </p>
          </div>
          <PrimaryButton onClick={() => history.push("/settings/billing")}>
            {t("settings.billing.action_upgrade")}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  if (uiState === "active") {
    return (
      <div className="rounded-md bg-green-50 p-4 ring-1 ring-inset ring-green-200">
        <div className="flex items-center">
          {subscription?.plan && (
            <PlanBadge plan={subscription.plan as unknown as Plan} />
          )}
          <span className="ml-2 text-sm text-gray-700">
            {subscription?.frequency}
          </span>
          <span className="ml-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-500/10">
            {t("settings.billing.status_active")}
          </span>
        </div>
      </div>
    );
  }

  if (uiState === "cancelling") {
    return (
      <div className="rounded-md bg-yellow-50 p-4 ring-1 ring-inset ring-yellow-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {subscription?.plan && (
              <PlanBadge plan={subscription.plan as unknown as Plan} />
            )}
            <span className="ml-2 inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-600 ring-1 ring-inset ring-yellow-500/10">
              {t("settings.billing.status_cancelling")}
            </span>
          </div>
          <PrimaryButton
            disabled={isReactivating}
            onClick={() => {
              if (subscription) {
                changeSubscription({
                  plan: subscription.plan,
                  frequency: subscription.frequency,
                });
              }
            }}
          >
            {t("settings.billing.action_reactivate")}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  if (uiState === "payment_pending") {
    return (
      <div className="rounded-md bg-red-50 p-4 ring-1 ring-inset ring-red-200">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-red-800">
            {t("settings.billing.status_payment_pending")}
          </span>
          <PrimaryButton
            disabled={isOpeningPortal}
            onClick={() => openBillingPortal()}
          >
            {t("settings.billing.action_manage")}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return null;
};
